<template>
  <div :class="[userAppStatus === 'VIEW_TOUR' ? 'min-h-screen' : 'px-4 pt-5 pb-10']">
    <template v-if="userAppStatus === 'VIEW_TOUR'">
      <GuideTour @action-success="onSuccessFinishGuide" />
    </template>
    <template v-else>
      <div class="flex items-center mb-5">
        <div class="flex items-center justify-between text-sm pr-2">
          {{ progressMessage }}
          <div>
            <span>{{ progressStep }}</span>/<span class="opacity-60">7</span>
          </div>
        </div>
        <div class="w-full h-1" :class="[$theme.placeBg]">
          <div class="h-1" :class="[$theme.inverseBaseBg]" :style="`width: ${progressPercent}%`"></div>
        </div>
        <div v-if="currentSystemTour && currentSystemTour.skip" class="pl-2">
          <button @click="onSkipStep(null)" class="font-semibold text-sm">
            {{ $t('skip') }}
          </button>
        </div>
      </div>
      <div v-for="systemTourItem in systemTour" :key="systemTourItem.id">
        <template v-if="userAppStatus === 'CHOICE_LANG' && systemTourItem.component === 'CHOICE_LANG'">
          <div v-html="systemTourItem.description" class="mb-3 prose max-w-none text-sm" :class="[$theme.baseTextColor]"></div>
          <div class="py-2">
            <a @click="onChangeLanguage(language)" href="javascript:void(0)"
               v-for="(language, langIndex) in languages" :key="langIndex"
               :class="[
                'flex justify-between items-center py-3',
                langIndex === (languages.length - 1) ? '' : `border-b ${$theme.cardBorderColor}`
              ]"
            >
              <div class="flex items-center">
                <div>
                  <img :src="`/img/flags/${language.language_code}.png`" class="w-9" />
                </div>
                <div class="flex flex-col pl-2">
                  <div>{{ language.title }}</div>
                  <div class="text-xs" :class="[$theme.cardLabelTextColor]">{{ language.native_title }}</div>
                </div>
              </div>
              <div>
                <template v-if="language.language_code === $user.language_code">
                  <CheckCircleIcon class="w-7" :class="[$theme.navActiveTextColor2]" />
                </template>
              </div>
            </a>
          </div>
          <div class="mt-4">
            <button
                @click="onApplyLang"
                type="button"
                class="flex w-full items-center m-yellow-bg rounded-full text-black justify-center px-3 py-3 font-semibold"
                :class="[
                  isFormProcessing ? 'opacity-50 pointer-events-none' : ''
                ]"
            >
              <template v-if="isFormProcessing">
                <ButtonSpinner />
              </template>
              {{ $t('choice_lang') }}
            </button>
          </div>
        </template>
        <template v-if="userAppStatus === 'PENDING_TERMS_ACCEPTANCE' && systemTourItem.component === 'PENDING_TERMS_ACCEPTANCE'">
          <div>
            <div class="py-3">
              <div class="text-2xl font-semibold">{{ systemTourItem.title || $t('accept_terms') }}</div>
              <div v-html="systemTourItem.description" class="text-sm mt-2 prose max-w-none opacity-70" :class="[$theme.baseTextColor]"></div>

              <dd class="mt-4 text-sm" :class="[$theme.baseTextColor]">
                <TermDocuments :documents="documents" />
              </dd>

              <div class="mt-4 relative flex items-start">
                <div class="flex h-6 items-center">
                  <input
                    v-model="termsForm.accept"
                    id="apply_terms"
                    type="checkbox"
                    class="h-4 w-4 rounded"
                    :class="[$theme.navActiveTextColor2, $theme.inputRingFocusColor, $theme.inputBorderColor, $theme.checkboxBgInactive]"
                  />
                </div>
                <div class="ml-3 text-sm leading-6">
                  <label for="apply_terms" class="font-medium" :class="[$theme.baseTextColor]">{{ $t('apply_terms') }}</label>
                  <p id="comments-description" class="text-sm" :class="[$theme.cardLabelTextColor]">{{ $t('apply_terms_desc') }}</p>
                </div>
              </div>
            </div>

            <div class="mt-4">
              <button
                  @click="onApplyTerms"
                  type="button"
                  class="flex w-full items-center m-yellow-bg rounded-full text-black justify-center px-3 py-3 font-semibold"
                  :class="[
                    isFormProcessing || !termsForm.accept ? 'opacity-50 pointer-events-none' : ''
                  ]"
              >
                <template v-if="isFormProcessing">
                  <ButtonSpinner />
                </template>
                {{ $t('apply_terms') }}
              </button>
            </div>
          </div>
        </template>
        <template v-if="userAppStatus === 'CONTACT_DETAILS_INCOMPLETE' && systemTourItem.component === 'CONTACT_DETAILS_INCOMPLETE'">
          <div>
            <div class="pt-3 pb-4">
              <div class="text-3xl font-semibold">{{ systemTourItem.title }}</div>
              <div v-html="systemTourItem.description" class="mt-2 opacity-70 prose max-w-none" :class="[$theme.baseTextColor, `prose-strong:${$theme.baseTextColor}`]"></div>

              <div class="mt-4">
                <div>
                  <label for="email" class="block text-sm font-medium leading-6" :class="[$theme.baseTextColor]">E-mail</label>
                  <div class="mt-2">
                    <input
                      v-model="contactForm.email"
                      id="email"
                      type="email"
                      class="block w-full rounded-full border-0 py-2 ring-0 ring-inset focus:ring-1 focus:ring-inset"
                      :class="[
                        $theme.baseTextColor,
                        $theme.inputPlaceTextColor,
                        $theme.inputRingFocusColor,
                        $theme.inputBorderColor,
                        $theme.inputRingColor,
                        $theme.inputBg,
                      ]"
                      placeholder="E-mail"
                    />
                    <p v-if="emailError" class="text-red-500 text-sm mt-1">{{ emailError }}</p>
                  </div>
                </div>
                <div class="mt-3">
                  <PhoneInput v-model="contactForm.phone" />
                  <p v-if="phoneError" class="text-red-500 text-sm mt-1">{{ phoneError }}</p>
                </div>
              </div>
            </div>

            <div class="mt-5">
              <button
                @click="updateContacts"
                type="button"
                class="flex w-full items-center m-yellow-bg rounded-full text-black justify-center px-3 py-3 font-semibold"
                :class="[
                  isFormProcessing ? 'opacity-50 pointer-events-none' : ''
                ]"
              >
                <template v-if="isFormProcessing">
                  <ButtonSpinner />
                </template>
                {{ $t('save_contacts') }}
              </button>
            </div>
          </div>
        </template>
        <template v-if="userAppStatus === 'EXCHANGE_NOT_CONNECTED' && systemTourItem.component === 'EXCHANGE_NOT_CONNECTED'">
          <div>
            <ConnectExchangeAccount
              :title="systemTourItem.title"
              :desc="systemTourItem.description"
              @action-success="onSuccessConnectAccount"
            />
          </div>
        </template>
        <template v-if="userAppStatus === 'PLAN_NOT_SELECTED' && systemTourItem.component === 'PLAN_NOT_SELECTED'">
          <SelectTariff
            :title="systemTourItem.title"
            :desc="systemTourItem.description"
            @load-payment-cards="onLoadPaymentCards"
          />
        </template>
        <template v-if="userAppStatus === 'NO_SIGNALS_SELECTED' && systemTourItem.component === 'NO_SIGNALS_SELECTED'">
          <div>
            <SelectSignals :title="systemTourItem.title" :desc="systemTourItem.description" />
            <div class="mt-5">
              <button
                  @click="completeSetup"
                  type="button"
                  class="flex w-full items-center justify-center px-3 py-3 font-semibold m-yellow-bg rounded-full text-black"
                  :class="[isFormProcessing ? 'opacity-50 pointer-events-none' : '']"
              >
                <template v-if="isFormProcessing">
                  <ButtonSpinner />
                </template>
                {{ $t('complete_setup') }}
              </button>
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import GuideTour from "@/components/GuideTour.vue";
import ButtonSpinner from "@/components/ButtonSpinner.vue";
import PhoneInput from "@/components/form/PhoneInput.vue";
import { validatePhoneNumber } from "@/helpers/validationHelper";
import {CheckCircleIcon} from "@heroicons/vue/20/solid";
import ConnectExchangeAccount from "@/components/exchange/ConnectExchangeAccount.vue";
import SelectTariff from "@/components/tariff/SelectTariff.vue";
import SelectSignals from "@/components/settings/SelectSignals.vue";
import TermDocuments from "@/components/TermDocuments.vue";

export default {
  components: {
    GuideTour,
    CheckCircleIcon,
    ButtonSpinner,
    PhoneInput,
    ConnectExchangeAccount,
    SelectTariff,
    SelectSignals,
    TermDocuments
  },

  data() {
    return {
      isFormProcessing: false,
      userAppStatus: this.$user.app_status,
      termsForm: {
        accept: false,
      },
      contactForm: {
        email: '',
        phone: ''
      },
      emailError: '',
      phoneError: '',

      languages: [],
      documents: [],
      systemTour: [],
      paymentCards: [],
    }
  },

  mounted() {
    this.loadSystemTours();

    this.loadLanguages();
    this.loadDocuments();
    this.checkStatus();

    if (window.Telegram?.WebApp?.BackButton) {
      this.checkBackButtonVisibility(this.userAppStatus);
      const backButton = window.Telegram.WebApp.BackButton;
      backButton.onClick(() => this.handleBackButton());
    }
  },

  watch: {
    userAppStatus(newStatus) {
      console.log(newStatus);
      this.checkStatus();
    }
  },

  computed: {
    currentSystemTour() {
      for (const item of this.systemTour) {
        if (this.userAppStatus === item.component) {
          return item;
        }
      }

      return null;
    },

    progressStep() {
      if (this.userAppStatus === 'CHOICE_LANG') {
        return 1;
      }

      if (this.userAppStatus === 'PENDING_TERMS_ACCEPTANCE') {
        return 2;
      }

      if (this.userAppStatus === 'CONTACT_DETAILS_INCOMPLETE') {
        return 3;
      }

      if (this.userAppStatus === 'EXCHANGE_NOT_CONNECTED') {
        return 4;
      }

      if (this.userAppStatus === 'PLAN_NOT_SELECTED') {
        return 5;
      }

      if (this.userAppStatus === 'NO_SIGNALS_SELECTED') {
        return 6;
      }

      if (this.userAppStatus === 'WAITING_LINKING_CARD') {
        return 7;
      }

      return 0;
    },

    progressPercent() {
      if (this.userAppStatus === 'CHOICE_LANG') {
        return 0;
      }

      if (this.userAppStatus === 'PENDING_TERMS_ACCEPTANCE') {
        return 10;
      }

      if (this.userAppStatus === 'CONTACT_DETAILS_INCOMPLETE') {
        return 20;
      }

      if (this.userAppStatus === 'EXCHANGE_NOT_CONNECTED') {
        return 40;
      }

      if (this.userAppStatus === 'PLAN_NOT_SELECTED') {
        return 60;
      }

      if (this.userAppStatus === 'NO_SIGNALS_SELECTED') {
        return 80;
      }

      if (this.userAppStatus === 'WAITING_LINKING_CARD') {
        return 90;
      }

      return 0;
    },
    progressMessage() {
      if (!this.currentSystemTour) {
        return '';
      }

      return this.currentSystemTour.note;
    }
  },

  methods: {
    forceBackButtonUpdate() {
      this.$nextTick(() => {
        this.checkBackButtonVisibility(this.userAppStatus);
      });
    },

    checkBackButtonVisibility(status) {
      if (window.Telegram?.WebApp?.BackButton) {
        const backButton = window.Telegram.WebApp.BackButton;
        const showForStatuses = [
          'PENDING_TERMS_ACCEPTANCE',
          'CONTACT_DETAILS_INCOMPLETE',
          'EXCHANGE_NOT_CONNECTED',
          'PLAN_NOT_SELECTED',
          'NO_SIGNALS_SELECTED'
        ];
        showForStatuses.includes(status) ? backButton.show() : backButton.hide();
      }
    },

    getPreviousStep() {
      const stepOrder = [
        'VIEW_TOUR',
        'CHOICE_LANG',
        'PENDING_TERMS_ACCEPTANCE',
        'CONTACT_DETAILS_INCOMPLETE',
        'EXCHANGE_NOT_CONNECTED',
        'PLAN_NOT_SELECTED',
        'NO_SIGNALS_SELECTED',
        'WAITING_LINKING_CARD',
        'COMPLETED'
      ];

      const currentIndex = stepOrder.indexOf(this.userAppStatus);
      return currentIndex > 0 ? stepOrder[currentIndex - 1] : null;
    },

    handleBackButton() {
      const previousStep = this.getPreviousStep();
      if (previousStep) {
        this.onSkipStep(previousStep);
      } else {
        window.Telegram.WebApp.close();
      }
    },

    onLoadPaymentCards(payload) {
      this.paymentCards = payload;
    },

    loadSystemTours() {
      this.$api.get('/system-tour').then((result) => {
        console.log(result.data.data)
        this.systemTour = result.data.data;
      }).catch(e => {
        alert(e.message)
        this.error = e
      })
    },

    checkStatus() {
      this.checkCompleteSetup();
    },

    onChangeLanguage(language) {
      this.$api.post('/user/update-language', {
        language_code: language.language_code
      }).then((result) => {
        console.log('Settings updated.', result);

        this.$i18n.locale = language.language_code;
        this.$user.language_code = language.language_code;
      }).catch(e => {
        alert(e.message);
        this.error = e;
        this.processing = null;
      });
    },

    onApplyLang() {
      this.isFormProcessing = true;

      this.$api.post('/user/apply-lang').then(result => {
        console.log('result', result);
        this.userAppStatus = 'PENDING_TERMS_ACCEPTANCE';
        this.isFormProcessing = false;
        this.forceBackButtonUpdate();
      });
    },

    onApplyTerms() {
      this.isFormProcessing = true;

      if (this.termsForm.accept) {
        this.$api.post('/user/apply-terms').then(result => {
          console.log('result', result);
          this.userAppStatus = 'CONTACT_DETAILS_INCOMPLETE';
          this.isFormProcessing = false;
          this.forceBackButtonUpdate();
        });
      }
    },

    onSuccessConnectAccount() {
      this.userAppStatus = 'PLAN_NOT_SELECTED';
      this.forceBackButtonUpdate();
    },

    onSuccessFinishGuide() {
      this.userAppStatus = 'CHOICE_LANG';
      this.forceBackButtonUpdate();
    },

    loadLanguages() {
      this.$api.get('/languages').then((result) => {
        console.log(result.data.data)
        this.languages = result.data.data;
      }).catch(e => {
        alert(e.message)
        this.error = e
      })
    },

    loadDocuments() {
      this.$api.get('/documents?bot_id=1').then(result => {
        this.documents = result.data.data;
      });
    },

    validateEmail(email) {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return regex.test(email);
    },

    updateContacts() {
      this.isFormProcessing = true;

      this.emailError = '';
      this.phoneError = '';

      if (!this.validateEmail(this.contactForm.email)) {
        this.emailError = this.$t('invalid_email');
        this.isFormProcessing = false;
        return;
      }

      if (!validatePhoneNumber(this.contactForm.phone)) {
        this.isFormProcessing = false;
        return;
      }

      this.$api.post('/user/update-contacts', this.contactForm).then(() => {
        this.userAppStatus = 'EXCHANGE_NOT_CONNECTED';
        this.isFormProcessing = false;
        this.forceBackButtonUpdate();
      });
    },

    onSkipStep(status = null) {
      let newStatus = status

      if (!newStatus) {
        console.log(this.currentSystemTour);

        if (this.currentSystemTour.component === 'CONTACT_DETAILS_INCOMPLETE') {
          newStatus = 'EXCHANGE_NOT_CONNECTED';
        }

        if (this.currentSystemTour.component === 'EXCHANGE_NOT_CONNECTED') {
          newStatus = 'PLAN_NOT_SELECTED';
        }

        if (this.currentSystemTour.component === 'PLAN_NOT_SELECTED') {
          newStatus = 'NO_SIGNALS_SELECTED';
        }

        if (this.currentSystemTour.component === 'NO_SIGNALS_SELECTED') {
          newStatus = 'COMPLETED';
        }
      }

      this.$api.post('/user/skip-tour-step', {
        status: newStatus
      }).then(() => {
        this.userAppStatus = newStatus;
        this.forceBackButtonUpdate();
      });
    },

    completeSetup() {
      this.isFormProcessing = true;

      this.$api.post('/user/complete-setup', {}).then(() => {
        window.location.href = '/';
      });
    },

    checkCompleteSetup() {
      this.$api.post('/user/check-complete-setup', {}).then((result) => {
        this.userAppStatus = result.data.status;

        if (this.userAppStatus === 'COMPLETED') {
          window.location.href = '/';
        }
      });
    }
  }
}
</script>

